import { ChartContainer } from '@lms/ui/chart';
import { cn } from '@lms/ui/utils';
import { type ReactNode, useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAccentColor } from '../brand';
import { formatCompact, formatNumber } from '../format';
import {
  OTHER_COLOR,
  type PivotRow,
  pivot,
  type Series,
  type SeriesSet,
} from '../series';
import {
  axisTick,
  barEndLabel,
  ChartEmpty,
  ChartLegendList,
  MAX_BAR_SIZE,
  SeriesTooltip,
  segmentShape,
  truncatedTick,
} from './parts';

const ROW_HEIGHT = 32;

export interface CategoryBarChartProps<R> {
  data: readonly R[];
  /** One bar per category. */
  category: (row: R) => string;
  value: (row: R) => number;
  /** Optional segments within each bar. */
  stack?: (row: R) => string;
  /**
   * Colours. With `colorBy: 'stack'` (default) the set is keyed on the stack
   * dimension and a legend is shown; with `colorBy: 'category'` it is keyed on
   * the category, so each bar wears its entity's colour and the segments are
   * only separated by gaps (e.g. courses within a curriculum).
   */
  seriesSet?: SeriesSet;
  colorBy?: 'stack' | 'category';
  /** Show at most this many bars; the rest are summed into one "Other" bar. */
  limit?: number;
  otherLabel?: string;
  /** A dashed reference, e.g. the average. */
  reference?: { value: number; label: string };
  valueFormatter?: (value: number) => string;
  locale?: string;
  labelWidth?: number;
  emptyLabel?: string;
  totalLabel?: string;
  /**
   * Makes the bars clickable (not the "Other" bar), e.g. to filter the page
   * by the clicked category. Gets the category name.
   */
  onSelect?: (category: string) => void;
  /** Extra tooltip line for a bar (label and value), e.g. what it averages. */
  footer?: (category: string) => ReactNode;
}

/**
 * One segment per distinct stack value, never folded: in category-colour
 * mode the segments only need keys and names (the bar's colour comes from
 * its category), so a curriculum with twenty courses keeps all twenty.
 */
function unfoldedSegments<R>(
  data: readonly R[],
  stack: ((row: R) => string) | undefined,
  color: string,
): SeriesSet {
  const names = [...new Set(data.map((r) => (stack ? stack(r) : '')))];
  const series: Series[] = names.map((name, i) => ({
    key: `seg${i}`,
    name: name || '–',
    color,
  }));
  const byName = new Map(names.map((n, i) => [n, series[i]]));
  const get = (n: string) => byName.get(n) ?? series[0];
  return {
    series,
    get,
    keyOf: (n) => get(n).key,
    config: Object.fromEntries(series.map((s) => [s.key, { label: s.name }])),
  };
}

/**
 * Horizontal bars, sorted by total with the total labelled at the bar end.
 * Horizontal so long location/curriculum names stay readable; the height
 * grows with the number of bars instead of squeezing them.
 */
export function CategoryBarChart<R>({
  data,
  category,
  value,
  stack,
  seriesSet,
  colorBy = 'stack',
  limit = 12,
  otherLabel = 'Other',
  reference,
  valueFormatter,
  locale,
  labelWidth = 128,
  emptyLabel = 'No data for this period',
  totalLabel,
  onSelect,
  footer,
}: CategoryBarChartProps<R>) {
  const fmt = valueFormatter ?? ((v: number) => formatNumber(v, locale));
  const accent = useAccentColor();

  const { rows, segments } = useMemo(() => {
    // Segments: the stack series when colouring by stack, otherwise every
    // distinct stack value (unfolded - colour comes from the category).
    const segments: SeriesSet =
      colorBy === 'stack' && seriesSet
        ? seriesSet
        : unfoldedSegments(data, stack, accent);
    let rows = pivot(data, {
      category,
      series: (r) => segments.keyOf(stack ? stack(r) : ''),
      value,
      keys: segments.series.map((s) => s.key),
    });
    if (rows.length > limit) {
      const tail = rows.slice(limit - 1);
      const other: PivotRow = { category: otherLabel, total: 0 };
      for (const s of segments.series) {
        other[s.key] = tail.reduce((sum, r) => sum + Number(r[s.key] ?? 0), 0);
      }
      other.total = tail.reduce((sum, r) => sum + r.total, 0);
      rows = [...rows.slice(0, limit - 1), other];
    }
    return { rows, segments };
  }, [
    data,
    category,
    value,
    stack,
    seriesSet,
    colorBy,
    limit,
    otherLabel,
    accent,
  ]);

  const height = rows.length * ROW_HEIGHT + 28 + (reference ? 20 : 0);
  if (!rows.some((r) => r.total > 0)) {
    return <ChartEmpty label={emptyLabel} height={160} />;
  }

  const keys = segments.series.map((s) => s.key);
  const colorOf = (s: Series, row: PivotRow) =>
    colorBy === 'category'
      ? row.category === otherLabel
        ? OTHER_COLOR
        : (seriesSet?.get(row.category).color ?? accent)
      : s.color;
  const showLegend = colorBy === 'stack' && seriesSet && stack;
  const lastKey = keys[keys.length - 1];

  return (
    <div className="flex flex-col gap-3">
      <ChartContainer
        config={segments.config}
        className={cn('aspect-auto w-full', onSelect && 'cursor-pointer')}
        style={{ height }}
      >
        <BarChart
          data={rows}
          onClick={(state) => {
            const label = state?.activeLabel;
            if (onSelect && label !== undefined && label !== otherLabel) {
              onSelect(String(label));
            }
          }}
          layout="vertical"
          margin={{ top: reference ? 20 : 0, right: 64, bottom: 0, left: 0 }}
          barCategoryGap={6}
          accessibilityLayer
        >
          <CartesianGrid horizontal={false} />
          <XAxis
            type="number"
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            tickCount={5}
            tick={axisTick}
            tickFormatter={(v: number) => formatCompact(v, locale)}
          />
          <YAxis
            type="category"
            dataKey="category"
            tickLine={false}
            axisLine={false}
            width={labelWidth}
            interval={0}
            tick={truncatedTick(labelWidth)}
          />
          <Tooltip
            cursor={{ fill: 'var(--muted)', opacity: 0.6 }}
            content={
              <SeriesTooltip
                series={segments.series}
                locale={locale}
                totalLabel={totalLabel}
                valueFormatter={fmt}
                seriesColor={colorOf}
                title={(row) => row.category}
                footer={footer && ((row) => footer(row.category))}
              />
            }
          />
          {reference && (
            <ReferenceLine
              x={reference.value}
              stroke="var(--muted-foreground)"
              strokeDasharray="4 3"
              ifOverflow="extendDomain"
              label={{
                value: reference.label,
                position: 'top',
                fontSize: 11,
                fill: 'var(--muted-foreground)',
              }}
            />
          )}
          {segments.series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.name}
              stackId="stack"
              fill={s.color}
              maxBarSize={MAX_BAR_SIZE}
              isAnimationActive={false}
              shape={segmentShape(s.key, keys, 'bar', (row) => colorOf(s, row))}
            >
              {s.key === lastKey && (
                <LabelList dataKey="total" content={barEndLabel(fmt)} />
              )}
            </Bar>
          ))}
        </BarChart>
      </ChartContainer>
      {showLegend && <ChartLegendList series={segments.series} />}
    </div>
  );
}
